/**
 * NCNP（国家算力一体化平台）跳转工具
 *
 * 文档协议来源：
 *  - 一体化算力服务对接说明 3.1 统一用户认证 / 5.5 welcome 通用前端跳转
 *  - task_id=login：运营商任意页面 → 国家平台登录页（登录失效或未登录场景）
 *
 * 设计要点：
 *  - 长三角作为完整业务中台，自做订购页 /orders/place；
 *    支付路径由后端按 loginSource 分流（NCNP / CA），前端按返回类型分支。
 *  - loginSource==NCNP 用户登录失效必须走 NCNP welcome，
 *    不能再跳长三角 ssoLoginUrl（CA SSO），否则国家平台无法识别会话来源。
 */
import website from "@/config/website";
import { getToken, getLoginSource } from "@/utils/auth";
import { ncnpAuthorizeUrl } from "@/api/index";

const NCNP_LOGIN_TASK = "login";
// state 防重放 + 登录后回跳目标，与 Entry.vue / NcnpCallback.vue 约定的键一致
const NCNP_STATE_KEY = "ncnpAuthState";
const NCNP_RETURN_KEY = "ncnpReturnAfterLogin";

/**
 * 返回当前 NCNP server URL（结尾不带斜杠）
 */
export function getNcnpServerUrl() {
  const base = website.ncnpServerUrl || "";
  return base.replace(/\/$/, "");
}

/**
 * 当前用户是否通过国家平台登录
 */
export function isNcnpUser() {
  return getLoginSource() === "NCNP";
}

/**
 * 跳到 NCNP 登录页（task_id=login）
 * @param {string} [returnPath] 登录完成后希望回到的本站路径，默认当前页
 * @param {string} [staleUserToken] 已失效的旧 userToken，按文档随 query 传回供国家平台清理
 */
export function gotoNcnpLogin(returnPath, staleUserToken) {
  const base = getNcnpServerUrl();
  if (!base) {
    // 没配 ncnpServerUrl 时退化为 CA SSO，避免空跳转
    window.location.href = website.ssoLoginUrl;
    return;
  }
  const params = new URLSearchParams();
  params.append("task_id", NCNP_LOGIN_TASK);
  const tokenForCleanup = staleUserToken || getToken() || "";
  if (tokenForCleanup) {
    params.append("user_token", tokenForCleanup);
  }
  const target = returnPath != null ? returnPath : currentReturnPath();
  if (target) {
    params.append("return_path", target);
  }
  window.location.href = `${base}/welcome?${params.toString()}`;
}

/**
 * 按 loginSource 路由"重新登录"目标：NCNP 用户去 NCNP welcome，其他走 CA SSO。
 * 用于 request/http/permission/Header 等通用兜底，避免直接散落 if/else。
 */
export function gotoReLogin(returnPath) {
  if (isNcnpUser()) {
    gotoNcnpLogin(returnPath);
    return;
  }
  window.location.href = website.ssoLoginUrl;
}

function currentReturnPath() {
  try {
    return window.location.pathname + window.location.search;
  } catch (e) {
    return "";
  }
}

/**
 * 收口"登录后回跳目标"为站内路径，拒绝绝对/协议相对 URL，默认 /compute。
 * 迁自 Entry.vue，供 Entry 与 startNcnpAuthorize 复用。
 */
export function sanitizeReturnPath(target) {
  if (typeof target !== "string" || target.length === 0) return "/compute";
  if (/^https?:\/\//i.test(target)) return "/compute";
  if (target.startsWith("//")) return "/compute";
  if (!target.startsWith("/")) return "/compute";
  return target;
}

/**
 * 主动发起 NCNP OAuth 授权码流程（运营商前端侧，文档 3.1 页面跳转触发认证）。
 * NCNP 已登录则在授权页静默回 code 到 /ncnp/callback；未登录则 NCNP 展示其登录页。
 * 真实回跳目标（如订购页 /orders/place?goods_id=x）经后端 state 存储透传，
 * redirect_uri 固定为 ncnp.redirect-uri(/ncnp/callback)。
 *
 * @param {string} redirect 登录完成后希望回到的站内路径
 * @returns {Promise<boolean>} 是否成功发起（true 时页面已 window.location 跳走；false 交调用方兜底）
 */
export async function startNcnpAuthorize(redirect) {
  const safe = sanitizeReturnPath(redirect);
  try {
    const res = await ncnpAuthorizeUrl({ redirect: safe });
    const data = res && res.data ? res.data : {};
    if (data.authorizeUrl) {
      if (data.state) {
        localStorage.setItem(NCNP_STATE_KEY, data.state);
      }
      localStorage.setItem(NCNP_RETURN_KEY, safe);
      window.location.href = data.authorizeUrl;
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

/**
 * 路由守卫统一的"未登录重定向"分流：
 *  - to.meta.ncnpAuthEntry 的外部入口页（订购/详情）→ 自动发起 NCNP 授权码流程，
 *    失败退化 gotoReLogin；
 *  - 其余受保护页 → 按 loginSource 走 NCNP welcome / CA SSO。
 * 供 permission.js 全局守卫与 Place.vue beforeRouteEnter 兜底共用。
 */
export function authGuardRedirect(to) {
  const path = to && to.fullPath ? to.fullPath : undefined;
  if (to && to.meta && to.meta.ncnpAuthEntry) {
    startNcnpAuthorize(path).then((ok) => {
      if (!ok) {
        gotoReLogin(path);
      }
    });
    return;
  }
  gotoReLogin(path);
}
